import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Check,
  ImagePlus,
  LoaderCircle,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { getAccessToken } from "../../../../awsAuth";
import PhotoDetails from "../../../PhotoDetails/PhotoDetails";
import "./BeforeAfter.scss";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

const READONLY_PREVIEW_LIMIT = 5;

function makeId() {
  return `ba_${Date.now()}_${Math.random()
    .toString(16)
    .slice(2)}`;
}

function normalizeItems(items) {
  return (Array.isArray(items) ? items : [])
    .map((item) => ({
      id: String(item?.id || makeId()),
      title: String(item?.title || "").trim(),
      beforeUrl: String(item?.beforeUrl || "").trim(),
      afterUrl: String(item?.afterUrl || "").trim(),
      createdAt:
        item?.createdAt || new Date().toISOString(),
      updatedAt:
        item?.updatedAt || new Date().toISOString(),
    }))
    .filter(
      (item) => item.beforeUrl || item.afterUrl
    );
}

async function uploadPhoto(
  file,
  onProgress
) {
  const token = await getAccessToken();

  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("photo", file);

    const xhr = new XMLHttpRequest();

    xhr.open(
      "POST",
      `${API_BASE}/api/listings/upload-photo`
    );

    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${token}`
    );

    xhr.upload.addEventListener(
      "progress",
      (event) => {
        if (!event.lengthComputable) return;

        const progress = Math.round(
          (event.loaded / event.total) * 100
        );

        onProgress?.(progress);
      }
    );

    xhr.addEventListener("load", () => {
      let data = {};

      try {
        data = JSON.parse(xhr.responseText || "{}");
      } catch {
        data = {};
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        reject(
          new Error(
            data?.error ||
              "Не вдалося завантажити фото"
          )
        );

        return;
      }

      const url =
        data.url ||
        data.photoUrl ||
        data.location ||
        data.urls?.[0] ||
        "";

      if (!url) {
        reject(
          new Error(
            "Сервер не повернув адресу фото"
          )
        );

        return;
      }

      onProgress?.(100);
      resolve(url);
    });

    xhr.addEventListener("error", () => {
      reject(
        new Error(
          "Помилка мережі під час завантаження"
        )
      );
    });

    xhr.addEventListener("abort", () => {
      reject(
        new Error("Завантаження скасовано")
      );
    });

    xhr.send(formData);
  });
}

function UploadProgress({
  progress = 0,
}) {
  const radius = 22;
  const circumference =
    2 * Math.PI * radius;

  const offset =
    circumference -
    (Math.max(0, Math.min(100, progress)) /
      100) *
      circumference;

  return (
    <div className="bpBeforeAfterUpload">
      <svg
        viewBox="0 0 54 54"
        aria-hidden="true"
      >
        <circle
          className="bpBeforeAfterUpload__track"
          cx="27"
          cy="27"
          r={radius}
        />

        <circle
          className="bpBeforeAfterUpload__progress"
          cx="27"
          cy="27"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>

      <strong>
        {progress >= 100 ? (
          <Check
            size={17}
            strokeWidth={3}
          />
        ) : (
          `${progress}%`
        )}
      </strong>

      <span>Завантаження</span>
    </div>
  );
}

export default function BeforeAfter({
  ownerId,
  readonly = false,
}) {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);

  const [draft, setDraft] = useState({
    title: "",
    beforeUrl: "",
    afterUrl: "",
  });

  const [adding, setAdding] =
    useState(false);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [uploadingKind, setUploadingKind] =
    useState("");

  const [uploadProgress, setUploadProgress] =
    useState({
      beforeUrl: 0,
      afterUrl: 0,
    });

    const [photoViewer, setPhotoViewer] = useState({
      open: false,
      photos: [],
      startIndex: 0,
    });

  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);

  const endpoint = useMemo(() => {
    if (readonly && ownerId) {
      return `${API_BASE}/api/business/before-after/user/${encodeURIComponent(
        ownerId
      )}`;
    }

    return `${API_BASE}/api/business/before-after/me`;
  }, [readonly, ownerId]);

  const visibleItems = useMemo(() => {
    if (!readonly) return items;

    return items.slice(
      0,
      READONLY_PREVIEW_LIMIT
    );
  }, [items, readonly]);

  const openCasePhotos = (
  item,
  startIndex = 0
) => {
  const photos = [
    item.beforeUrl
      ? {
          src: item.beforeUrl,
          alt: `${item.title || "Результат роботи"} — До`,
        }
      : null,

    item.afterUrl
      ? {
          src: item.afterUrl,
          alt: `${item.title || "Результат роботи"} — Після`,
        }
      : null,
  ].filter(Boolean);

  if (!photos.length) return;

  const safeStartIndex = Math.max(
    0,
    Math.min(
      startIndex,
      photos.length - 1
    )
  );

  setPhotoViewer({
    open: true,
    photos,
    startIndex: safeStartIndex,
  });
};

const closePhotoViewer = () => {
  setPhotoViewer((current) => ({
    ...current,
    open: false,
  }));
};

  const openAll = () => {
    if (ownerId) {
      navigate(
        `/profile/${encodeURIComponent(
          ownerId
        )}/before-after`
      );

      return;
    }

    navigate("/business/before-after");
  };

  const saveItems = async (
    nextItems
  ) => {
    if (readonly) return;

    setSaving(true);

    try {
      const token =
        await getAccessToken();

      const res = await fetch(
        `${API_BASE}/api/business/before-after/me`,
        {
          method: "PUT",
          headers: {
            "Content-Type":
              "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            items:
              normalizeItems(nextItems),
          }),
        }
      );

      const data = await res
        .json()
        .catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error ||
            "Не вдалося зберегти"
        );
      }

      setItems(
        normalizeItems(
          data?.items || nextItems
        )
      );
    } catch (error) {
      console.error(
        "[BeforeAfter] save error:",
        error
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);

        const token =
          await getAccessToken().catch(
            () => null
          );

        const headers = token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {};

        const res = await fetch(
          endpoint,
          { headers }
        );

        const data = await res
          .json()
          .catch(() => ({}));

        if (!alive) return;

        setItems(
          res.ok
            ? normalizeItems(
                data?.items || []
              )
            : []
        );
      } catch (error) {
        console.error(
          "[BeforeAfter] load error:",
          error
        );

        if (alive) {
          setItems([]);
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, [endpoint]);

  const pickPhoto = async (
    kind,
    file
  ) => {
    if (!file || readonly) return;
    if (uploadingKind) return;

    setUploadingKind(kind);

    setUploadProgress((current) => ({
      ...current,
      [kind]: 0,
    }));

    try {
      const localPreview =
        URL.createObjectURL(file);

      setDraft((current) => ({
        ...current,
        [kind]: localPreview,
      }));

      const url = await uploadPhoto(
        file,
        (progress) => {
          setUploadProgress(
            (current) => ({
              ...current,
              [kind]: progress,
            })
          );
        }
      );

      setDraft((current) => ({
        ...current,
        [kind]: url,
      }));

      URL.revokeObjectURL(localPreview);
    } catch (error) {
      console.error(
        "[BeforeAfter] upload error:",
        error
      );

      setDraft((current) => ({
        ...current,
        [kind]: "",
      }));
    } finally {
      setTimeout(() => {
        setUploadingKind("");

        setUploadProgress(
          (current) => ({
            ...current,
            [kind]: 0,
          })
        );
      }, 350);
    }
  };

  const addItem = async () => {
    if (readonly) return;
    if (uploadingKind) return;

    const nextItem = {
      id: makeId(),
      title: draft.title.trim(),
      beforeUrl: draft.beforeUrl,
      afterUrl: draft.afterUrl,
      createdAt:
        new Date().toISOString(),
      updatedAt:
        new Date().toISOString(),
    };

    if (
      !nextItem.beforeUrl &&
      !nextItem.afterUrl
    ) {
      return;
    }

    const next = [
      nextItem,
      ...items,
    ];

    setItems(next);

    setDraft({
      title: "",
      beforeUrl: "",
      afterUrl: "",
    });

    setAdding(false);

    await saveItems(next);
  };

  const cancelAdding = () => {
    if (uploadingKind) return;

    setAdding(false);

    setDraft({
      title: "",
      beforeUrl: "",
      afterUrl: "",
    });
  };

  const removeItem = async (id) => {
    if (readonly) return;

    const next = items.filter(
      (item) => item.id !== id
    );

    setItems(next);
    await saveItems(next);
  };

  const updateTitle = async (
    id,
    title
  ) => {
    if (readonly) return;

    const next = items.map((item) =>
      item.id === id
        ? {
            ...item,
            title,
            updatedAt:
              new Date().toISOString(),
          }
        : item
    );

    setItems(next);
    await saveItems(next);
  };

  if (loading) {
    return (
      <div className="bpBeforeAfter">
        <div className="bpBeforeAfter__state">
          <LoaderCircle
            size={20}
            className="bpBeforeAfter__spinner"
          />

          Завантаження кейсів...
        </div>
      </div>
    );
  }

  if (
    readonly &&
    !items.length
  ) {
    return null;
  }

  return (
    <div
      className={[
        "bpBeforeAfter",
        readonly
          ? "bpBeforeAfter--readonly"
          : "bpBeforeAfter--editable",
      ].join(" ")}
    >
      <div className="bpBeforeAfter__head">
        <div>
          <strong>До / Після</strong>

          <span>
            Результати виконаних робіт
          </span>
        </div>

        {readonly ? (
          items.length > 0 && (
            <button
              type="button"
              className="bpBeforeAfter__allButton"
              onClick={openAll}
            >
              Переглянути всі
            </button>
          )
        ) : (
          !adding && (
            <button
              type="button"
              onClick={() =>
                setAdding(true)
              }
            >
              <Plus
                size={18}
                strokeWidth={2.7}
              />

              Додати
            </button>
          )
        )}
      </div>

      {adding && !readonly && (
        <div className="bpBeforeAfterDraft">
          <input
            value={draft.title}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                title:
                  event.target.value,
              }))
            }
            placeholder="Назва кейсу, наприклад: Полірування автомобіля"
          />

          <div className="bpBeforeAfterDraft__photos">
            <button
              type="button"
              className={[
                "bpBeforeAfterDraft__photo",
                draft.beforeUrl
                  ? "has-photo"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() =>
                beforeInputRef.current?.click()
              }
              disabled={
                Boolean(uploadingKind)
              }
            >
              {draft.beforeUrl ? (
                <img
                  src={draft.beforeUrl}
                  alt="Фото до"
                />
              ) : (
                <span className="bpBeforeAfterDraft__placeholder">
                  <ImagePlus size={25} />
                  <strong>Фото до</strong>
                  <em>
                    Оберіть початковий стан
                  </em>
                </span>
              )}

              <span className="bpBeforeAfterDraft__label">
                До
              </span>

              {uploadingKind ===
                "beforeUrl" && (
                <UploadProgress
                  progress={
                    uploadProgress.beforeUrl
                  }
                />
              )}
            </button>

            <button
              type="button"
              className={[
                "bpBeforeAfterDraft__photo",
                draft.afterUrl
                  ? "has-photo"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() =>
                afterInputRef.current?.click()
              }
              disabled={
                Boolean(uploadingKind)
              }
            >
              {draft.afterUrl ? (
                <img
                  src={draft.afterUrl}
                  alt="Фото після"
                />
              ) : (
                <span className="bpBeforeAfterDraft__placeholder">
                  <ImagePlus size={25} />
                  <strong>
                    Фото після
                  </strong>
                  <em>
                    Покажіть результат
                  </em>
                </span>
              )}

              <span className="bpBeforeAfterDraft__label">
                Після
              </span>

              {uploadingKind ===
                "afterUrl" && (
                <UploadProgress
                  progress={
                    uploadProgress.afterUrl
                  }
                />
              )}
            </button>
          </div>

          <input
            ref={beforeInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file =
                event.target.files?.[0];

              pickPhoto(
                "beforeUrl",
                file
              );

              event.target.value = "";
            }}
          />

          <input
            ref={afterInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file =
                event.target.files?.[0];

              pickPhoto(
                "afterUrl",
                file
              );

              event.target.value = "";
            }}
          />

          <div className="bpBeforeAfterDraft__actions">
            <button
              type="button"
              onClick={cancelAdding}
              disabled={
                saving ||
                Boolean(uploadingKind)
              }
            >
              <X size={17} />
              Скасувати
            </button>

            <button
              type="button"
              onClick={addItem}
              disabled={
                saving ||
                Boolean(uploadingKind) ||
                (!draft.beforeUrl &&
                  !draft.afterUrl)
              }
            >
              {saving ? (
                <LoaderCircle
                  size={17}
                  className="bpBeforeAfter__spinner"
                />
              ) : (
                <Save size={17} />
              )}

              Зберегти
            </button>
          </div>
        </div>
      )}

      {!items.length && !adding ? (
        <div className="bpBeforeAfter__empty">
          <strong>
            Кейсів поки немає
          </strong>

          <span>
            Додайте фотографії «До» і
            «Після», щоб показати результат
            роботи.
          </span>
        </div>
      ) : (
        <div className="bpBeforeAfter__list">
          {visibleItems.map((item) => (
            <article
              key={item.id}
              className="bpBeforeAfterCard"
            >
              <div className="bpBeforeAfterCompare">
                <button
                  type="button"
                  className="bpBeforeAfterCompare__side"
                  onClick={() =>
                    openCasePhotos(item, 0)
                  }
                  aria-label="Переглянути фото до"
                >
                  {item.beforeUrl ? (
                    <img
                      src={item.beforeUrl}
                      alt={`${item.title || "Результат роботи"} — До`}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <span />
                  )}

                  <em>До</em>

                  {item.beforeUrl && (
                    <span className="bpBeforeAfterCompare__zoomHint">
                      Натисніть для перегляду
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  className="bpBeforeAfterCompare__side"
                  onClick={() =>
                    openCasePhotos(
                      item,
                      item.beforeUrl ? 1 : 0
                    )
                  }
                  aria-label="Переглянути фото після"
                >
                  {item.afterUrl ? (
                    <img
                      src={item.afterUrl}
                      alt={`${item.title || "Результат роботи"} — Після`}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <span />
                  )}

                  <em>Після</em>

                  {item.afterUrl && (
                    <span className="bpBeforeAfterCompare__zoomHint">
                      Натисніть для перегляду
                    </span>
                  )}
                </button>
              </div>

              <div className="bpBeforeAfterCard__bottom">
                {readonly ? (
                  <strong>
                    {item.title ||
                      "Результат роботи"}
                  </strong>
                ) : (
                  <input
                    value={item.title}
                    onChange={(event) => {
                      const value =
                        event.target.value;

                      setItems((current) =>
                        current.map((row) =>
                          row.id === item.id
                            ? {
                                ...row,
                                title: value,
                              }
                            : row
                        )
                      );
                    }}
                    onBlur={(event) =>
                      updateTitle(
                        item.id,
                        event.target.value
                      )
                    }
                    placeholder="Назва кейсу"
                  />
                )}

                {!readonly && (
                  <button
                    type="button"
                    onClick={() =>
                      removeItem(item.id)
                    }
                    aria-label="Видалити кейс"
                  >
                    <Trash2
                      size={17}
                      strokeWidth={2.5}
                    />
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
            )}

      <PhotoDetails
        isOpen={photoViewer.open}
        photos={photoViewer.photos}
        startIndex={photoViewer.startIndex}
        onClose={closePhotoViewer}
      />
    </div>
  );
}